import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackThemeToggled } from '../utils/analytics';

/**
 * ThemeContext provides theme state and toggle function to all components
 * @typedef {Object} ThemeContextValue
 * @property {'light' | 'dark'} theme - Current theme
 * @property {() => void} toggleTheme - Function to toggle between light and dark theme
 * @property {boolean} isDark - Whether dark theme is currently active
 */

/** @type {React.Context<ThemeContextValue | undefined>} */
const ThemeContext = createContext(undefined);

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Initialize with system preference immediately
  const getInitialTheme = () => {
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
        return savedTheme;
      }
    } catch (error) {
      console.warn('Unable to read theme preference from localStorage:', error);
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [isInitialized, setIsInitialized] = useState(false);

  // Apply theme to document body and save to localStorage
  useEffect(() => {
    // Apply theme to document body
    if (theme === 'dark') {
      document.body.setAttribute('data-theme', 'dark');
    } else {
      document.body.removeAttribute('data-theme');
    }

    // Only save to localStorage after initial load to avoid overwriting system preference detection
    if (isInitialized) {
      try {
        localStorage.setItem('theme', theme);
      } catch (error) {
        console.warn('Unable to save theme preference to localStorage:', error);
      }
    }
  }, [theme, isInitialized]);

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      // Track theme toggle
      if (isInitialized) {
        trackThemeToggled(newTheme);
      }
      return newTheme;
    });
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if no manual preference is saved and component is initialized
      let hasSavedTheme = false;
      try {
        hasSavedTheme = Boolean(localStorage.getItem('theme'));
      } catch (error) {
        console.warn('Unable to read theme preference from localStorage:', error);
      }
      if (!hasSavedTheme && isInitialized) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isInitialized]);

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};